import React from 'react';
import { Link } from 'react-router-dom';
import { LANDING_WEB_ORIGIN } from '../../config/domainConfig';
import {
  ComplianceFooterVariant,
  normalizeComplianceFooterVariant,
} from './utils/complianceFooter';

export interface ComplianceFooterProps {
  variant?: ComplianceFooterVariant | string | null;
  formattedAddress?: string | string[] | null;
  textColor: string;
  showBadge?: boolean;
  workspaceId?: string;
  containerWidth?: string;
  className?: string;
}

const UnsubPreview: React.FC<{ className?: string }> = ({ className }) => (
  <span className={`underline cursor-default ${className ?? ''}`}>Unsubscribe from these emails</span>
);

const BadgePreview: React.FC<{ className?: string }> = ({ className }) => (
  <a
    href={LANDING_WEB_ORIGIN}
    target="_blank"
    rel="noopener noreferrer"
    className={`no-underline hover:underline ${className ?? ''}`}
    style={{ color: 'inherit' }}
  >
    Powered by Scubamail
  </a>
);

const AddressLines: React.FC<{
  formattedAddress?: string | string[] | null;
  lineClassName?: string;
}> = ({ formattedAddress, lineClassName = 'text-[12px] mb-1' }) => {
  if (!formattedAddress) return null;
  if (Array.isArray(formattedAddress)) {
    return (
      <>
        {formattedAddress.map((line, idx) => (
          <p key={idx} className={lineClassName} style={{ color: 'inherit' }}>
            {line}
          </p>
        ))}
      </>
    );
  }
  return (
    <p className={lineClassName} style={{ color: 'inherit' }}>
      {formattedAddress}
    </p>
  );
};

const MissingAddress: React.FC<{ workspaceId?: string }> = ({ workspaceId }) => (
  <div className="bg-coral-50/50 border border-dashed border-coral-200 rounded-xl p-4 mb-4 backdrop-blur-sm mx-auto max-w-sm">
    <p className="text-[12px] text-coral-600 font-medium mb-2 flex items-center justify-center gap-1.5">
      Physical address is mandatory for compliance
    </p>
    {workspaceId ? (
      <Link
        to={`/workspace/${workspaceId}/settings/general`}
        className="text-[11px] text-coral-500 underline hover:text-coral-700 font-medium"
      >
        Add your address in Workspace Settings
      </Link>
    ) : (
      <span className="text-[11px] text-coral-500">Add your address in Workspace Settings</span>
    )}
  </div>
);

export const ComplianceFooter: React.FC<ComplianceFooterProps> = ({
  variant,
  formattedAddress,
  textColor,
  showBadge = true,
  workspaceId,
  containerWidth,
  className = '',
}) => {
  const v = normalizeComplianceFooterVariant(variant);
  const hasAddress = Boolean(
    formattedAddress &&
      (Array.isArray(formattedAddress) ? formattedAddress.length > 0 : String(formattedAddress).trim())
  );

  const wrapperStyle: React.CSSProperties = {
    width: containerWidth,
    maxWidth: '100%',
    color: textColor,
  };

  if (v === 'divided') {
    return (
      <div
        className={`mx-auto text-center px-4 pt-5 pb-6 mb-8 mt-6 ${className}`}
        style={wrapperStyle}
      >
        {hasAddress ? (
          <div className="mb-3">
            <AddressLines formattedAddress={formattedAddress} lineClassName="text-[12px] mb-1" />
          </div>
        ) : (
          <MissingAddress workspaceId={workspaceId} />
        )}
        <p className="text-[11px] flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <UnsubPreview />
          {showBadge && (
            <>
              <span className="opacity-35">|</span>
              <BadgePreview />
            </>
          )}
        </p>
      </div>
    );
  }

  // classic (default)
  return (
    <div className={`mx-auto text-center px-4 py-8 mb-8 mt-4 opacity-90 ${className}`} style={wrapperStyle}>
      {hasAddress ? (
        <AddressLines formattedAddress={formattedAddress} />
      ) : (
        <MissingAddress workspaceId={workspaceId} />
      )}
      <p className="text-[11px] mt-3 mb-4">
        <UnsubPreview />
      </p>
      {showBadge && (
        <p className="text-[12px] font-medium mt-6">
          <BadgePreview />
        </p>
      )}
    </div>
  );
};

export default ComplianceFooter;
